import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import Recorder from "./Recorder";
import PcpInterview from "./PcpInterview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Wand2, Loader2, Save, Sparkles, FileHeart, ShieldAlert, FileText, Plus, Trash2, Target, Upload } from "lucide-react";

const toArr = (txt) => String(txt || "").split("\n").map((s) => s.trim()).filter(Boolean);
const lines = (arr) => (Array.isArray(arr) ? arr.join("\n") : arr || "");

const TOP = [
  ["Member Name", "member_name"],
  ["Medicaid ID", "member_id"],
  ["Record #", "record_number"],
  ["Date of Birth", "date_of_birth"],
  ["Diagnosis", "diagnosis"],
  ["PCP Completed On", "plan_date"],
  ["Plan Meeting Date", "plan_meeting_date"],
  ["Effective Date", "effective_date"],
  ["Review Date", "review_date"],
];
const DOMAINS = [
  ["Daily Life and Employment", "domain_daily_life"],
  ["Community Living", "domain_community_living"],
  ["Safety and Security", "domain_safety_security"],
  ["Healthy Living", "domain_healthy_living"],
  ["Social and Spirituality", "domain_social_spirituality"],
  ["Citizenship and Advocacy", "domain_citizenship_advocacy"],
];
const OWN_WORDS = [
  ["What do you want to work on / accomplish? (member's own words)", "want_to_work_on", 2],
  ["What strengths do you currently have? (member's own words)", "strengths", 2],
  ["What are the obstacles to meeting your goals? (member's own words)", "obstacles", 2],
  ["Long-Term Goal (~12 months)", "long_range_goal", 2],
];
const CRISIS = [
  ["Triggers / Warning Signs", "crisis_triggers"],
  ["Prevention Strategies", "crisis_prevention"],
  ["Stabilization & Support Contacts", "crisis_stabilization"],
  ["Crisis Follow-Up", "crisis_followup"],
];
const EMPTY_GOAL = {
  timeframe: "", goal: "",
  psr_staff_interventions: [], group_home_interventions: [], individual_natural_supports: [],
};

const cleanAP = (ap) =>
  (ap || [])
    .map((g) => ({
      timeframe: g.timeframe || "",
      goal: g.goal || "",
      psr_staff_interventions: toArr(lines(g.psr_staff_interventions)),
      group_home_interventions: toArr(lines(g.group_home_interventions)),
      individual_natural_supports: toArr(lines(g.individual_natural_supports)),
    }))
    .filter((g) => g.goal.trim() || g.psr_staff_interventions.length || g.group_home_interventions.length || g.individual_natural_supports.length);

export default function PcpCreator({ onSaved }) {
  const [raw, setRaw] = useState("");
  const [inputMode, setInputMode] = useState("interview"); // "interview" | "describe" | "structure"
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pcp, setPcp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pcp/extract-text", fd);
      setRaw((r) => (r ? r + "\n\n" + data.text : data.text));
      toast.success("File read — review the text below, then tap Structure my PCP.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read that file.");
    } finally {
      setUploading(false);
    }
  };

  const upd = (k) => (e) => setPcp((p) => ({ ...p, [k]: e.target.value }));
  const updItem = (idx, key, value) =>
    setPcp((p) => {
      const ap = [...(p.action_plan || [])];
      ap[idx] = { ...ap[idx], [key]: value };
      return { ...p, action_plan: ap };
    });
  const addGoal = () => setPcp((p) => ({ ...p, action_plan: [...(p.action_plan || []), { ...EMPTY_GOAL }] }));
  const removeGoal = (idx) => setPcp((p) => ({ ...p, action_plan: (p.action_plan || []).filter((_, i) => i !== idx) }));

  const buildFull = () => {
    const action_plan = cleanAP(pcp.action_plan);
    const goals = action_plan.map((g) => g.goal).filter(Boolean);
    const interventions = [];
    action_plan.forEach((g) => g.psr_staff_interventions.forEach((i) => i && interventions.push(i)));
    return { ...pcp, action_plan, goals, interventions };
  };

  const downloadFile = async (kind) => {
    const isDocx = kind === "docx";
    try {
      const res = await api.post(`/pcp/${kind}`, { pcp: buildFull() }, { responseType: "blob" });
      const type = isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PCP-${(pcp.member_name || "member").replace(/\s+/g, "_")}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(isDocx ? "PCP Word document downloaded — it follows the NC PCP template." : "PCP PDF downloaded!");
    } catch {
      toast.error(`Could not build the PCP ${isDocx ? "Word file" : "PDF"}.`);
    }
  };

  const generate = async () => {
    if (!raw.trim()) {
      toast.info("Describe the member first — talk or type below.");
      return;
    }
    runGenerate(raw, inputMode);
  };

  const [interviewKey, setInterviewKey] = useState(0);

  const runGenerate = async (text, mode, overrides = {}, draftId = null) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/pcp/generate", { raw_input: text, mode });
      const jobId = data.job_id;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: s } = await api.get(`/pcp/generate/${jobId}`);
          if (s.status === "done" && s.pcp) {
            const p = { ...s.pcp };
            Object.entries(overrides).forEach(([k, v]) => { if (v) p[k] = v; });
            if (!Array.isArray(p.action_plan) || p.action_plan.length === 0) p.action_plan = [{ ...EMPTY_GOAL }];
            setPcp(p);
            setGenerating(false);
            if (draftId) {
              api.delete(`/pcp/drafts/${draftId}`).catch(() => {});
              setInterviewKey((k) => k + 1);
            }
            toast.success("Draft PCP ready — review every section before saving.");
            setTimeout(() => document.querySelector('[data-testid="pcp-editor"]')?.scrollIntoView({ behavior: "smooth" }), 100);
          } else if (s.status === "error") {
            setGenerating(false);
            toast.error(s.error || "PCP generation failed. Please try again.");
          } else if (tries > 80) {
            setGenerating(false);
            toast.error("PCP is taking too long. Please try again.");
          } else {
            setTimeout(poll, 3000);
          }
        } catch {
          if (tries > 80) {
            setGenerating(false);
            toast.error("Lost connection while building the PCP. Please try again.");
          } else {
            setTimeout(poll, 3000);
          }
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setGenerating(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const persist = async (target, existingId) => {
    setSaving(true);
    setDupOpen(false);
    const full = buildFull();
    const memberName = pcp.member_name.trim();
    const payload = {
      name: memberName,
      member_id: pcp.member_id || "",
      diagnosis: pcp.diagnosis || "",
      pcp_summary: pcp.summary || "",
      goals: full.goals,
      interventions: full.interventions,
      review_date: pcp.review_date || "",
      pcp: { ...full, member_name: memberName },
    };
    try {
      if (target === "update" && existingId) {
        await api.put(`/consumers/${existingId}`, payload);
        toast.success(`Updated ${memberName}'s existing PCP.`);
      } else {
        await api.post("/consumers", payload);
        toast.success("PCP saved to the consumer roster.");
      }
      setPcp(null);
      setRaw("");
      setDupMatch(null);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!pcp?.member_name?.trim()) {
      toast.info("Member name is required to save the PCP.");
      return;
    }
    // duplicate-name check before saving
    try {
      const { data } = await api.get("/consumers/lookup", { params: { name: pcp.member_name.trim() } });
      if (data.match) {
        setDupMatch(data.match);
        setDupOpen(true);
        return;
      }
    } catch { /* if lookup fails, fall through to a normal save */ }
    persist("new");
  };

  const IntvBox = ({ idx, label, field, testid }) => (
    <div className="space-y-1">
      <Label className="text-[9px] font-semibold text-[#4A6B53] uppercase tracking-wider">{label} — one per line</Label>
      <Textarea
        data-testid={testid}
        value={lines(pcp.action_plan[idx][field])}
        onChange={(e) => updItem(idx, field, e.target.value.split("\n"))}
        rows={2}
        className="bg-white border-[#DCE5DF] text-sm resize-y"
      />
    </div>
  );

  return (
    <div data-testid="pcp-creator">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
          <FileHeart className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
            Create a Person-Centered Plan
          </h1>
          <p className="text-sm text-[#7A8C82]">
            Answer a few questions (or just talk) — I'll build a compliant PCP (NC 2022 Guidance) you can review, save, and download as the official NC PCP form.
          </p>
        </div>
      </div>

      {/* input canvas */}
      <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm overflow-hidden">
        <div className={`px-6 sm:px-10 pt-8 flex flex-col items-center text-center border-b border-[#E2DFD8] bg-gradient-to-b from-white to-[#F9F9F6] ${inputMode === "interview" ? "pb-6" : "pb-8"}`}>
          {/* mode toggle */}
          <div className={`inline-flex flex-wrap justify-center p-1 rounded-full bg-[#EAE8E3] border border-[#E2DFD8] ${inputMode === "interview" ? "" : "mb-6"}`}>
            <button
              data-testid="pcp-mode-interview"
              onClick={() => setInputMode("interview")}
              className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${inputMode === "interview" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"}`}
            >
              Answer questions
            </button>
            <button
              data-testid="pcp-mode-describe"
              onClick={() => setInputMode("describe")}
              className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${inputMode === "describe" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"}`}
            >
              Just talk
            </button>
            <button
              data-testid="pcp-mode-structure"
              onClick={() => setInputMode("structure")}
              className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${inputMode === "structure" ? "bg-white text-[#121814] shadow-sm" : "text-[#7A8C82]"}`}
            >
              I already have a PCP
            </button>
          </div>
          {inputMode !== "interview" && (<>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter text-[#121814] max-w-xl leading-tight">
            {inputMode === "structure" ? "Import an existing PCP" : "Tell me about the member"}
          </h2>
          <p className="mt-3 text-[#7A8C82] max-w-lg leading-relaxed">
            {inputMode === "structure"
              ? "Upload a Word, PDF, or text file — or paste the plan below — and I'll organize it into the structured PCP fields you can review and save."
              : "Cover their strengths, life across the six domains (daily life & employment, community living, safety, healthy living, social/spiritual, citizenship), what they want, their obstacles, their goals, and what to do in a crisis. I'll write SMART goals and PSR skill-building interventions that follow NC DHHS & Alliance Health PSR (H2017) rules."}
          </p>
          {inputMode === "structure" ? (
            <div className="mt-6">
              <label data-testid="pcp-upload-label" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-base font-medium shadow-[0_8px_16px_rgba(74,107,83,0.25)] cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" strokeWidth={1.5} />}
                {uploading ? "Reading file…" : "Upload PCP file"}
                <input data-testid="pcp-upload-input" type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={handleFile} disabled={uploading} />
              </label>
              <p className="mt-2 text-xs text-[#7A8C82]">PDF, Word (.docx), or text file — or just paste below.</p>
            </div>
          ) : (
            <div className="mt-8">
              <Recorder disabled={generating} onTranscript={(t) => setRaw((r) => (r ? r + " " + t : t))} />
            </div>
          )}
          </>)}
        </div>
        {inputMode === "interview" ? (
          <PcpInterview key={interviewKey} generating={generating} onComplete={(text, about, draftId) => runGenerate(text, "interview", about, draftId)} />
        ) : (
        <div className="p-6 sm:p-8">
          <label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">
            {inputMode === "structure" ? "The existing PCP text" : "Describe the member & their plan"}
          </label>
          <Textarea
            data-testid="pcp-raw-input"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={inputMode === "structure" ? 8 : 5}
            placeholder={inputMode === "structure"
              ? "Paste the existing Person-Centered Plan here (or upload a file above). I'll pull the client details, life domains, goals, and interventions into the right fields."
              : "e.g. Juilie Bedford, 65, lives in a family care home, attends Eagle PSR. Strong about taking meds, easy to get along with, knows Raleigh well. Wants less leg pain so he can walk more, stay sober, smoke fewer cigarettes. Obstacle: leg pain limits walking. Long-term goal: improve daily functioning with pain management. Goals: pain diary 3x/week; one new health activity per week; three new coping skills for cravings."}
            className="mt-2 bg-[#F9F9F6] border-[#E2DFD8] text-base leading-relaxed resize-y"
          />
          <Button
            data-testid="generate-pcp-btn"
            onClick={generate}
            disabled={generating}
            className="mt-4 h-12 px-8 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-base font-medium shadow-[0_8px_16px_rgba(74,107,83,0.25)] transition-colors"
          >
            {generating
              ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {inputMode === "structure" ? "Organizing PCP…" : "Building PCP…"}</>
              : <><Wand2 className="w-5 h-5 mr-2" strokeWidth={1.5} /> {inputMode === "structure" ? "Structure my PCP" : "Build PCP"}</>}
          </Button>
          {generating && (
            <p className="mt-3 text-xs text-[#7A8C82]" data-testid="pcp-generating-hint">
              {inputMode === "structure" ? "Organizing your PCP into the structured fields" : "Building a full Person-Centered Plan"} — this can take up to a minute. You can leave this tab open.
            </p>
          )}
        </div>
        )}
      </div>

      {/* editor */}
      {pcp && (
        <div className="mt-8 bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-6 lg:p-8 fade-up" data-testid="pcp-editor">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b border-[#E2DFD8]">
            <div>
              <h3 className="font-display text-xl font-bold text-[#121814]">Review the plan</h3>
              <p className="text-xs text-[#7A8C82]">Edit any section, then save it to the roster.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="pcp-docx-btn" onClick={() => downloadFile("docx")} variant="outline" className="h-11 px-5 rounded-full border-[#4A6B53] text-[#4A6B53] bg-white hover:bg-[#4A6B53] hover:text-white">
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> NC PCP Form (Word)
              </Button>
              <Button data-testid="pcp-pdf-btn" onClick={() => downloadFile("pdf")} variant="outline" className="h-11 px-5 rounded-full border-[#E2DFD8] bg-white hover:bg-[#EAE8E3]">
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> PDF
              </Button>
              <Button data-testid="save-pcp-btn" onClick={save} disabled={saving} className="h-11 px-6 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save PCP</>}
              </Button>
            </div>
          </div>

          {/* header fields */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {TOP.map(([label, key]) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</Label>
                <Input data-testid={`pcp-field-${key}`} value={pcp[key] || ""} onChange={upd(key)} className="h-11 bg-[#F9F9F6] border-[#E2DFD8] text-sm" />
              </div>
            ))}
          </div>

          {/* Life Domains Assessed */}
          <div className="mt-8">
            <p className="text-xs font-bold text-[#4A6B53] uppercase tracking-wider mb-3">Life Domains Assessed</p>
            <div className="grid md:grid-cols-2 gap-4">
              {DOMAINS.map(([label, key]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</Label>
                  <Textarea data-testid={`pcp-field-${key}`} value={pcp[key] || ""} onChange={upd(key)} rows={3} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
                </div>
              ))}
            </div>
          </div>

          {/* Own words */}
          <div className="mt-8 space-y-5">
            {OWN_WORDS.map(([label, key, rows]) => (
              <div key={key} className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</Label>
                <Textarea data-testid={`pcp-field-${key}`} value={pcp[key] || ""} onChange={upd(key)} rows={rows} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
              </div>
            ))}
          </div>

          {/* Action Plan — structured short-term goals */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-[#4A6B53] uppercase tracking-wider flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5" strokeWidth={1.5} /> Action Plan — Short-Term Goals
              </p>
              <Button data-testid="pcp-add-goal-btn" onClick={addGoal} variant="outline" className="h-9 px-4 rounded-full border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white text-xs">
                <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> Add goal
              </Button>
            </div>
            <div className="space-y-5">
              {(pcp.action_plan || []).map((g, idx) => (
                <div key={idx} data-testid={`pcp-goal-${idx}`} className="rounded-xl border border-[#DCE5DF] bg-[#F4F6F3] p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] font-bold text-[#4A6B53]">Short-Term Goal {idx + 1}</span>
                    <button data-testid={`pcp-remove-goal-${idx}`} onClick={() => removeGoal(idx)} className="text-[#C26D5C] hover:text-[#a5503f] transition-colors" title="Remove goal">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3">
                    <div className="space-y-1">
                      <Label className="text-[9px] font-semibold text-[#7A8C82] uppercase tracking-wider">Timeframe</Label>
                      <Input data-testid={`pcp-goal-${idx}-timeframe`} value={g.timeframe || ""} onChange={(e) => updItem(idx, "timeframe", e.target.value)} placeholder="e.g. Within 6 months" className="h-10 bg-white border-[#DCE5DF] text-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[9px] font-semibold text-[#7A8C82] uppercase tracking-wider">SMART Short-Term Goal</Label>
                      <Textarea data-testid={`pcp-goal-${idx}-goal`} value={g.goal || ""} onChange={(e) => updItem(idx, "goal", e.target.value)} rows={2} className="bg-white border-[#DCE5DF] text-sm resize-y" />
                    </div>
                  </div>
                  <IntvBox idx={idx} label="PSR Staff Interventions" field="psr_staff_interventions" testid={`pcp-goal-${idx}-psr`} />
                  <IntvBox idx={idx} label="Group Home / Residential Staff Interventions" field="group_home_interventions" testid={`pcp-goal-${idx}-grouphome`} />
                  <IntvBox idx={idx} label="Individual and/or Natural Support Actions" field="individual_natural_supports" testid={`pcp-goal-${idx}-individual`} />
                </div>
              ))}
            </div>
          </div>

          {/* natural supports */}
          <div className="mt-6 space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Natural Supports</Label>
            <Textarea data-testid="pcp-field-natural_supports" value={pcp.natural_supports || ""} onChange={upd("natural_supports")} rows={2} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
          </div>

          {/* crisis plan */}
          <div className="mt-6 rounded-xl border border-[#F0D9D2] bg-[#FBF3F0] p-5">
            <p className="text-[10px] font-bold text-[#C26D5C] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> Enhanced Crisis Intervention Plan
            </p>
            <div className="space-y-4">
              {CRISIS.map(([label, key]) => (
                <div key={key} className="space-y-1.5">
                  <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</Label>
                  <Textarea data-testid={`pcp-field-${key}`} value={pcp[key] || ""} onChange={upd(key)} rows={2} className="bg-white border-[#E2DFD8] text-sm resize-y" />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Responsible Parties / Signatures <span className="text-[#A9B3AC] normal-case">(one person per line)</span></Label>
            <Textarea data-testid="pcp-field-responsible_parties" value={pcp.responsible_parties || ""} onChange={upd("responsible_parties")} rows={4} placeholder={"One person per line, e.g.\nJuilard Bedford — Member\nDr. Quinn Baker — QP"} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
          </div>

          <div className="mt-6 space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Summary</Label>
            <Textarea data-testid="pcp-field-summary" value={pcp.summary || ""} onChange={upd("summary")} rows={3} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
          </div>
        </div>
      )}

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent data-testid="pcp-duplicate-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">This client is already in your roster</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#3A453F] -mt-1">
            A client named <span className="font-semibold">{dupMatch?.name}</span>
            {dupMatch?.member_id ? <span className="text-[#7A8C82]"> · {dupMatch.member_id}</span> : null} already exists.
            Do you want to update their existing PCP, or save this as a separate new client?
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button data-testid="dup-cancel-btn" variant="outline" onClick={() => setDupOpen(false)} className="rounded-full border-[#E2DFD8]">
              Cancel
            </Button>
            <Button data-testid="dup-save-new-btn" variant="outline" onClick={() => persist("new")} disabled={saving} className="rounded-full border-[#4A6B53] text-[#4A6B53]">
              Save as new client
            </Button>
            <Button data-testid="dup-update-btn" onClick={() => persist("update", dupMatch?.id)} disabled={saving} className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Update ${dupMatch?.name || "client"}'s PCP`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!pcp && !generating && (
        <div className="flex flex-col items-center text-center py-14 text-[#7A8C82]">
          <Sparkles className="w-8 h-8 mb-3 text-[#D4A373]" strokeWidth={1.5} />
          <p className="text-sm max-w-xs leading-relaxed">Your compliant PCP draft will appear here after you tap "Build PCP."</p>
        </div>
      )}
    </div>
  );
}
